// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Textarea, SearchableDropdown, Radio } from '@/components/ui'
import {
  useAnnouncementDetail,
  useCreateAnnouncement,
  useUpdateAnnouncement,
} from '@/hooks/useAnnouncements'
import { useProperties } from '@/hooks/useProperties'
import type { PropertyUnit } from '@/hooks/useUnits'
import { usePropertyUnits } from '@/hooks/useUnits'
import { toISOString, toDatetimeLocal } from './announcementDateHelpers'

const TITLE_MAX = 255

interface AnnouncementFormData {
  title: string
  body: string
  published_at: string
  expires_at: string
  target_type: 'all' | 'property' | 'unit'
  target_id: string
}

export function AnnouncementFormPage() {
  const { t } = useTranslation('announcements')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const announcementId = Number(id)
  const navigate = useNavigate()

  const { data: existing, isLoading: isLoadingExisting } = useAnnouncementDetail(
    isEditing ? announcementId : 0
  )
  const createAnnouncement = useCreateAnnouncement()
  const updateAnnouncement = useUpdateAnnouncement()

  const { data: propertiesData } = useProperties(1)
  // Wrap the `?? ||` logical expression in its own useMemo so the reference
  // is stable across renders (deps of subsequent useMemo blocks).
  const properties = useMemo(() => propertiesData?.data ?? [], [propertiesData])

  const [unitSearch, setUnitSearch] = useState('')

  /**
   * Unit targeting is property-scoped (owner, 2026-08-05: "all page should have
   * property selected"). Pick the building, then the unit inside it — the same
   * cascade ParcelFormPage and MaintenanceFormPage already use.
   *
   * This replaces a flat account-wide list fetched from `GET /api/units?all=true`
   * — a route that never existed. It 404'd on every render since 2026-03-19 and
   * the failure was swallowed, so this picker silently offered NO units at all
   * for five months.
   *
   * The unit list is searched and paged ON THE SERVER through
   * SearchableDropdown's async contract (`onSearch` + `onLoadMore`/`hasMore`),
   * not bulk-loaded. Filtering client-side over one 20-row page would leave
   * unit 21 onwards unreachable behind a list that looks complete.
   */
  const [unitPropertyId, setUnitPropertyId] = useState('')
  const [unitPage, setUnitPage] = useState(1)
  const [accumulatedUnits, setAccumulatedUnits] = useState<PropertyUnit[]>([])

  const { data: unitsData, isLoading: isLoadingUnits } = usePropertyUnits(
    Number(unitPropertyId) || 0,
    unitPage,
    undefined,
    unitSearch || undefined,
  )

  // A new query starts a new list; anything else appends the next page.
  useEffect(() => {
    setUnitPage(1)
    setAccumulatedUnits([])
  }, [unitSearch, unitPropertyId])

  useEffect(() => {
    if (!unitsData?.data) return
    setAccumulatedUnits((prev) =>
      unitPage === 1 ? unitsData.data : [...prev, ...unitsData.data.filter((u) => !prev.some((p) => p.id === u.id))],
    )
  }, [unitsData, unitPage])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<AnnouncementFormData>({
    defaultValues: {
      title: '',
      body: '',
      published_at: '',
      expires_at: '',
      target_type: 'all',
      target_id: '',
    },
  })

  const targetType = watch('target_type')
  const titleValue = watch('title')
  const titleRemaining = TITLE_MAX - (titleValue?.length ?? 0)

  useEffect(() => {
    if (isEditing && existing) {
      reset({
        title: existing.title,
        body: existing.body,
        published_at: toDatetimeLocal(existing.published_at),
        expires_at: existing.expires_at ? toDatetimeLocal(existing.expires_at) : '',
        target_type: existing.target_type,
        target_id: existing.target_id ? String(existing.target_id) : '',
      })
      // Editing a unit-targeted announcement: the payload carries the unit id
      // but the picker is scoped by property, so without this the building
      // reads blank and the unit list comes back empty — the saved target would
      // look lost. `target_property_id` exists on the detail view for exactly
      // this.
      if (existing.target_type === 'unit' && existing.target_property_id) {
        setUnitPropertyId(String(existing.target_property_id))
      }
    }
  }, [isEditing, existing, reset])

  const propertyOptions = useMemo(
    () => [
      { value: '', label: `— ${t('form.property_label')} —` },
      ...properties.map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [properties, t]
  )

  // No client-side filtering here: the query runs on the server, so filtering
  // again locally would hide rows the user explicitly searched for.
  const unitOptions = useMemo(() => {
    const opts = [
      { value: '', label: `— ${t('form.unit_label')} —` },
      ...accumulatedUnits.map((u) => ({ value: String(u.id), label: u.name })),
    ]
    // Editing: the saved unit may not be on the page currently loaded (or may be
    // filtered out by a search the user has since typed). Without this the
    // dropdown falls back to its placeholder and the saved target reads as
    // empty, so saving an unrelated edit would silently retarget the
    // announcement. Same guard ParcelFormPage uses for its saved customer.
    const savedId = isEditing && existing?.target_type === 'unit' ? String(existing.target_id ?? '') : ''
    if (savedId && !opts.some((o) => o.value === savedId)) {
      opts.splice(1, 0, { value: savedId, label: existing?.target_label ?? savedId })
    }
    return opts
  }, [accumulatedUnits, isEditing, existing, t])

  const onSubmit = async (formData: AnnouncementFormData) => {
    const payload = {
      title: formData.title,
      body: formData.body,
      published_at: toISOString(formData.published_at),
      expires_at: formData.expires_at ? toISOString(formData.expires_at) : null,
      target_type: formData.target_type,
      target_id:
        formData.target_type !== 'all' && formData.target_id
          ? Number(formData.target_id)
          : null,
    }

    if (isEditing) {
      await updateAnnouncement.mutateAsync({ id: announcementId, ...payload })
      navigate(`/announcements/${announcementId}`)
    } else {
      const result = await createAnnouncement.mutateAsync(payload)
      navigate(`/announcements/${result.id}`)
    }
  }

  if (isEditing && isLoadingExisting) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="h-64 animate-pulse bg-base-200 rounded" />
        </Card>
      </div>
    )
  }

  const breadcrumbItems = isEditing && existing
    ? [
        { label: t('title'), href: '/announcements' },
        { label: existing.title, href: `/announcements/${announcementId}` },
        { label: tc('actions.edit') },
      ]
    : [
        { label: t('title'), href: '/announcements' },
        { label: t('new_button') },
      ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        title={isEditing ? tc('actions.edit') : t('new_button')}
        description={t('subtitle')}
      />

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div>
            <Input
              label={t('form.title_label')}
              placeholder={t('form.title_placeholder')}
              maxLength={TITLE_MAX}
              error={errors.title?.message}
              {...register('title', {
                required: tc('errors.required'),
                maxLength: { value: TITLE_MAX, message: tc('errors.required') },
              })}
            />
            <p className="mt-1 text-xs text-base-content/60 text-right">
              {t('form.title_counter', { count: titleRemaining })}
            </p>
          </div>

          {/* Body */}
          <div>
            <Textarea
              label={t('form.body_label')}
              rows={6}
              error={errors.body?.message}
              {...register('body', { required: tc('errors.required') })}
            />
            <p className="mt-1 text-xs text-base-content/60">{t('form.body_hint')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Published Date & Time */}
            <Input
              label={t('form.publish_label')}
              type="datetime-local"
              error={errors.published_at?.message}
              {...register('published_at', { required: tc('errors.required') })}
            />

            {/* Expiry Date & Time */}
            <div>
              <Input
                label={t('form.expires_label')}
                type="datetime-local"
                {...register('expires_at')}
              />
              <p className="mt-1 text-xs text-base-content/60">{t('form.expires_hint')}</p>
            </div>
          </div>

          {/* Target */}
          <div>
            <label className="label">
              <span className="label-text font-medium">{t('form.target_label')}</span>
            </label>
            <div className="flex flex-col gap-3">
              {(['all', 'property', 'unit'] as const).map((type) => (
                <label key={type} className="flex items-center gap-3 cursor-pointer">
                  <Radio
                    variant="primary"
                    value={type}
                    {...register('target_type')}
                  />
                  <span>
                    {type === 'all'
                      ? t('form.target_all')
                      : type === 'property'
                      ? t('form.target_property')
                      : t('form.target_unit')}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Property selector */}
          {targetType === 'property' && (
            <Controller
              name="target_id"
              control={control}
              rules={{ required: tc('errors.required') }}
              render={({ field, fieldState }) => (
                <SearchableDropdown
                  label={t('form.property_label')}
                  placeholder={`— ${t('form.property_label')} —`}
                  options={propertyOptions}
                  value={field.value}
                  onChange={field.onChange}
                  name="target_id_property"
                  error={fieldState.error?.message}
                />
              )}
            />
          )}

          {/* Unit selector — property first, then the unit inside it. The
              property is NOT submitted; it only scopes the unit list, which is
              why it is local state rather than a form field. */}
          {targetType === 'unit' && (
            <>
              <SearchableDropdown
                label={t('form.property_label')}
                placeholder={`— ${t('form.property_label')} —`}
                options={propertyOptions}
                value={unitPropertyId}
                onChange={(value) => {
                  setUnitPropertyId(value)
                  // A unit from the previous building must not survive the
                  // switch — it would submit a target the user cannot see.
                  setValue('target_id', '')
                }}
                name="unit_property_scope"
              />
              <Controller
                name="target_id"
                control={control}
                rules={{ required: tc('errors.required') }}
                render={({ field, fieldState }) => (
                  <SearchableDropdown
                    label={t('form.unit_label')}
                    placeholder={`— ${t('form.unit_label')} —`}
                    options={unitOptions}
                    value={field.value}
                    onChange={field.onChange}
                    onSearch={setUnitSearch}
                    isLoading={isLoadingUnits}
                    hasMore={
                      !!unitsData?.meta &&
                      unitsData.meta.current_page < unitsData.meta.total_pages
                    }
                    onLoadMore={() => setUnitPage((p) => p + 1)}
                    name="target_id_unit"
                    disabled={!unitPropertyId}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </>
          )}

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isEditing ? updateAnnouncement.isPending : createAnnouncement.isPending}
            >
              {t('form.save_button')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  )
}
